import { Koi } from '@ksyos/koi';
import type { RegisterParams } from '@common/workflow/serviceActivityRegistry';
import { CustomControlCode } from '@common/enums';
import { osasNoteSections } from '@common/validation/customControls/osas/osasNoteSchema';
import {
    osasResendPatientInviteDraftSchema,
    osasResendPatientInviteFinalSchema,
} from '@common/validation/customControls/osas/osasResendPatientInviteSchema';
import { ServiceActivityRegistry } from '@common/workflow/serviceActivityRegistry';
import {
    osasAdditionalExaminationDraftSchema,
    osasAdditionalExaminationFinalSchema,
} from './osasAdditionalExaminationSchema';
import {
    osasCloseUnfinishedExaminationDraftSchema,
    osasCloseUnfinishedExaminationFinalSchema,
} from './osasCloseUnfinishedExaminationSchema';
import { osasCloudPatExaminationReadSchema, osasCloudPatExaminationSchema } from './osasCloudPatExaminationSchema';
import { osasConsultReadSchema } from './osasConsultSchemas/osasConsultReadSchema';
import { osasConsultDraftSchema, osasConsultFinalSchema } from './osasConsultSchemas/osasConsultSchema';
import { osasEvaluationReadSchema } from './osasEvaluationSchemas/osasEvaluationReadSchema';
import { osasEvaluationDraftSchema, osasEvaluationFinalSchema } from './osasEvaluationSchemas/osasEvaluationSchema';
import { osasExternalReferralLetterSchema } from './osasExternalReferralLetterSchema';
import {
    osasFollowUpConsultDraftSchema,
    osasFollowUpConsultFinalSchema,
    OsasFollowUpConsultReadSchema,
} from './osasFollowUpConsultSchema';
import { osasLifestyleQuestionnaireReadSchema, osasLifestyleQuestionnaireSchema } from './osasLifestyleFormSchema';
import { osasMijnKsyosQuestionnaireSchema } from './osasMijnKsyosQuestionnaireSchema';
import { osasMraLetterSchema } from './osasMraLetterSchema';
import { osasReferralReadSchema } from './osasReferralSchemas/osasReferralReadSchema';
import { osasReferralDraftSchema, osasReferralFinalSchema } from './osasReferralSchemas/osasReferralSchema';
import {
    osasResendMijnKsyosQuestionnairesDraftSchema,
    osasResendMijnKsyosQuestionnairesFinalSchema,
    osasResendMijnKsyosQuestionnairesReadSchema,
} from './osasResendMijnKsyosQuestionnairesSchema';
import {
    osasShowMijnKsyosQuestionnairesReadSchema,
    osasShowMijnKsyosQuestionnairesSchema,
} from './osasShowMijnKsyosQuestionnairesSchema';
import { osasTreatmentChoiceReadSchema } from './osasTreatmentChoice/osasTreatmentChoiceReadSchema';
import {
    osasTreatmentChoiceDraftSchema,
    osasTreatmentChoiceFinalSchema,
} from './osasTreatmentChoice/osasTreatmentChoiceSchema';
import { osasZorgDomeinReferralReadSchema } from './osasZorgDomeinReferralSchemas/osasZorgDomeinReferralReadSchema';
import {
    osasZorgDomeinReferralSchemaDraft,
    osasZorgDomeinReferralSchemaFinal,
} from './osasZorgDomeinReferralSchemas/osasZorgDomeinReferralSchema';

const registryElements: RegisterParams[] = [
    {
        customControlCode: CustomControlCode.OSASReferral,
        draft: osasReferralDraftSchema,
        final: osasReferralFinalSchema,
        readSections: osasReferralReadSchema,
        version: 1,
    },
    {
        customControlCode: CustomControlCode.OSASShowMijnKsyosQuestionnairesInClientsafe,
        draft: osasShowMijnKsyosQuestionnairesSchema,
        final: osasShowMijnKsyosQuestionnairesSchema,
        readSections: osasShowMijnKsyosQuestionnairesReadSchema,
        version: 1,
    },
    {
        customControlCode: CustomControlCode.OSASMijnKsyosHsdqQuestionnaire,
        draft: osasMijnKsyosQuestionnaireSchema,
        final: osasMijnKsyosQuestionnaireSchema,
        version: 1,
    },
    {
        customControlCode: CustomControlCode.OSASMijnKsyosEssQuestionnaire,
        draft: osasMijnKsyosQuestionnaireSchema,
        final: osasMijnKsyosQuestionnaireSchema,
        version: 1,
    },
    {
        customControlCode: CustomControlCode.OSASZorgDomeinReferral,
        draft: osasZorgDomeinReferralSchemaDraft,
        final: osasZorgDomeinReferralSchemaFinal,
        readSections: osasZorgDomeinReferralReadSchema,
        version: 1,
    },
    {
        customControlCode: CustomControlCode.OSASLifestyleForm,
        draft: osasLifestyleQuestionnaireSchema,
        final: osasLifestyleQuestionnaireSchema,
        readSections: osasLifestyleQuestionnaireReadSchema,
        version: 1,
    },
    {
        customControlCode: CustomControlCode.OSASCloudPatExamination,
        draft: osasCloudPatExaminationSchema,
        final: osasCloudPatExaminationSchema,
        readSections: osasCloudPatExaminationReadSchema,
        version: 1,
    },
    {
        customControlCode: CustomControlCode.OSASRequestAdditionalExamination,
        draft: osasAdditionalExaminationDraftSchema,
        final: osasAdditionalExaminationFinalSchema,
        version: 1,
    },
    {
        customControlCode: CustomControlCode.OSASCloseUnfinishedExamination,
        draft: osasCloseUnfinishedExaminationDraftSchema,
        final: osasCloseUnfinishedExaminationFinalSchema,
        version: 1,
    },
    {
        customControlCode: CustomControlCode.OSASConsult,
        draft: osasConsultDraftSchema,
        final: osasConsultFinalSchema,
        readSections: osasConsultReadSchema,
        version: 1,
    },
    {
        customControlCode: CustomControlCode.OSASFollowUpConsult,
        draft: osasFollowUpConsultDraftSchema,
        final: osasFollowUpConsultFinalSchema,
        readSections: OsasFollowUpConsultReadSchema,
        version: 1,
    },
    {
        customControlCode: CustomControlCode.OSASEvaluation,
        draft: osasEvaluationDraftSchema,
        final: osasEvaluationFinalSchema,
        readSections: osasEvaluationReadSchema,
        version: 1,
    },
    {
        customControlCode: CustomControlCode.OSASMraLetter,
        draft: osasMraLetterSchema,
        final: osasMraLetterSchema,
        version: 1,
    },
    {
        customControlCode: CustomControlCode.OSASTreatmentChoice,
        draft: osasTreatmentChoiceDraftSchema,
        final: osasTreatmentChoiceFinalSchema,
        readSections: osasTreatmentChoiceReadSchema,
        version: 1,
    },
    {
        customControlCode: CustomControlCode.OSASResendMijnKsyosPatientInvite,
        draft: osasResendPatientInviteDraftSchema,
        final: osasResendPatientInviteFinalSchema,
        version: 1,
    },
    {
        customControlCode: CustomControlCode.OSASExternalReferralLetter,
        draft: osasExternalReferralLetterSchema,
        final: osasExternalReferralLetterSchema,
        version: 1,
    },
    {
        customControlCode: CustomControlCode.OSASResendMijnKsyosQuestionnaires,
        draft: osasResendMijnKsyosQuestionnairesDraftSchema,
        final: osasResendMijnKsyosQuestionnairesFinalSchema,
        readSections: osasResendMijnKsyosQuestionnairesReadSchema,
        version: 1,
    },
    {
        customControlCode: CustomControlCode.OSASRequestFollowUpExamination,
        draft: osasAdditionalExaminationDraftSchema,
        final: osasAdditionalExaminationFinalSchema,
        version: 1,
    },
    {
        customControlCode: CustomControlCode.OSASCloseUnfinishedFollowUpExamination,
        draft: osasCloseUnfinishedExaminationDraftSchema,
        final: osasCloseUnfinishedExaminationFinalSchema,
        version: 1,
    },
    {
        customControlCode: CustomControlCode.OSASCloudPatFollowUpExamination,
        draft: osasCloudPatExaminationSchema,
        final: osasCloudPatExaminationSchema,
        readSections: osasCloudPatExaminationReadSchema,
        version: 1,
    },
    {
        customControlCode: CustomControlCode.OSASNote,
        sections: osasNoteSections,
        version: 1,
    },
    {
        customControlCode: CustomControlCode.OSASCPAPChosen,
        draft: Koi.object().required(),
        final: Koi.object().required(),
        version: 1,
    },
];

ServiceActivityRegistry.instance.registerAll(registryElements);
